import { useMemo, useState } from 'react'
import { Stack, useTheme } from '@mui/material'

import Plus from 'assets/icons/Plus Button.png'

import { DocumentTypeNonDropdown } from './invoice-documents-table.styles'
import { documentFullName } from './invoice-documents-table.utils'
import FileUpload from 'components/file-upload/file-upload.component'
import ConfirmationDialog from 'components/confirmation-dialog/confirmation-dialog.component'
import LoadConfirmationProcessing from 'components/load-confirmation-processing/load-confirmation-processing.component'
import TranspotsLoading from 'components/transpots-loading/transpots-loading.component'
import {
  useAddInvoiceDocumentMutation,
  useAddPaymentDocumentMutation,
  useLazyListInvoicedocumentsQuery,
  useUpdateInvoiceStatusMutation,
} from 'store/invoices/invoices.api'
import {
  DocumentType,
  InvoiceType,
  isToBeInvoicedWorkspaceTab,
} from 'store/invoices/invoices.types'
import {
  useApplyLoadConfirmationReprocessMutation,
  useCancelLoadConfirmationReprocessMutation,
  useReprocessLoadConfirmationForOrderMutation,
} from 'store/task-automation/task-automation.api'

interface IProps {
  documentType: string
  orderId: string
  invoiceId?: string
  invoiceType?: InvoiceType
  condensed?: boolean
  variant?: 'receivables' | 'carrier'
  handleClose?: () => void
  refetchDocuments?: () => void
  refetchPaymentDocuments?: () => void
}

const InvoiceDocumentsTableDocType = ({
  documentType,
  orderId,
  invoiceId,
  invoiceType,
  condensed = false,
  variant = 'receivables',
  handleClose,
  refetchDocuments,
  refetchPaymentDocuments,
}: IProps) => {
  const theme = useTheme()

  const [uploadOpen, setUploadOpen] = useState(false)
  const [moveToPaidConfirmOpen, setMoveToPaidConfirmOpen] = useState(false)
  const [ratesChangedConfirmOpen, setRatesChangedConfirmOpen] = useState(false)
  const [lcProcessingOpen, setLcProcessingOpen] = useState(false)
  const [pendingLcUpdatePayload, setPendingLcUpdatePayload] = useState<
    Record<string, unknown> | null
  >(null)
  const [pendingLcId, setPendingLcId] = useState<string | null>(null)
  const [error, setError] = useState('')

  const [addDocument, { isLoading: isAddLoading, isError: isAddError }] =
    useAddInvoiceDocumentMutation()

  const [reprocessLc, { isLoading: isLcReprocessLoading }] =
    useReprocessLoadConfirmationForOrderMutation()
  const [applyLcReprocess, { isLoading: isLcApplyLoading }] =
    useApplyLoadConfirmationReprocessMutation()
  const [cancelLcReprocess] = useCancelLoadConfirmationReprocessMutation()

  const [
    addPaymentDocument,
    { isLoading: isPaymentLoading, isError: isPaymentError },
  ] = useAddPaymentDocumentMutation()

  const [updateInvoiceStatus, { isLoading: isMarkPaidLoading }] =
    useUpdateInvoiceStatusMutation()
  const [listOrderDocuments] = useLazyListInvoicedocumentsQuery()

  const isPaymentDoc = useMemo(
    () =>
      documentType === 'REMITTANCE' ||
      documentType === 'PAYMENT_RECEIPT' ||
      documentType === 'OTHER',
    [documentType]
  )

  const isupload = useMemo(() => {
    if (documentType === 'LC') {
      return variant !== 'carrier'
    }
    if (documentType === 'INVOICE') {
      return variant === 'carrier'
    }
    return (
      documentType === 'BOL' ||
      documentType === 'POD' ||
      isPaymentDoc
    )
  }, [documentType, isPaymentDoc, variant])

  const handleOpen = () => {
    setUploadOpen(true)
  }

  const maybeUpdateCarrierStatusAfterUpload = async () => {
    if (variant !== 'carrier' || !invoiceId) return
    if (invoiceType && !isToBeInvoicedWorkspaceTab(invoiceType)) return

    // POD upload from To Delivery → Invoice Pending (TO_BE_INVOICED)
    if (documentType === 'POD' && invoiceType === 'TO_DELIVER') {
      await updateInvoiceStatus({
        id: invoiceId,
        status: 'TO_BE_INVOICED',
      }).unwrap()
      handleClose?.()
      return
    }

    // Invoice + POD both present → Unpaid (due date set by backend)
    const docs = await listOrderDocuments({ orderId }).unwrap()
    const hasInvoice = (docs?.INVOICE?.length ?? 0) > 0
    const hasPod = (docs?.POD?.length ?? 0) > 0
    if (!hasInvoice || !hasPod) return

    await updateInvoiceStatus({
      id: invoiceId,
      status: 'NOT_PAID',
    }).unwrap()
    handleClose?.()
  }

  const handleConfirmMoveToPaid = async () => {
    if (!invoiceId) {
      setMoveToPaidConfirmOpen(false)
      return
    }
    try {
      await updateInvoiceStatus({
        id: invoiceId,
        status: 'PAID',
      }).unwrap()
      setMoveToPaidConfirmOpen(false)
      handleClose?.()
    } catch {
      setError('Failed to update invoice status. Please try again.')
      setMoveToPaidConfirmOpen(false)
    }
  }

  const handleLcReprocessUpload = async (file: File) => {
    setUploadOpen(false)
    setLcProcessingOpen(true)
    setError('')
    try {
      const result = await reprocessLc({
        orderId,
        file,
        invoiceId,
      }).unwrap()

      if (
        result.status === 'confirmation_required' &&
        result.updatePayload &&
        result.pendingId
      ) {
        setPendingLcUpdatePayload(result.updatePayload)
        setPendingLcId(result.pendingId)
        setRatesChangedConfirmOpen(true)
        return
      }

      if (refetchDocuments) {
        await refetchDocuments()
      }
    } catch (uploadError: any) {
      const detail =
        uploadError?.data?.detail ||
        uploadError?.error ||
        'Failed to process load confirmation. Please try again.'
      setError(typeof detail === 'string' ? detail : 'Something went wrong.')
      setUploadOpen(true)
    } finally {
      setLcProcessingOpen(false)
    }
  }

  const handleConfirmRateChangeUpdate = async () => {
    if (!pendingLcUpdatePayload || !pendingLcId) {
      setRatesChangedConfirmOpen(false)
      return
    }
    setRatesChangedConfirmOpen(false)
    setLcProcessingOpen(true)
    try {
      await applyLcReprocess({
        orderId,
        updatePayload: pendingLcUpdatePayload,
        pendingId: pendingLcId,
        invoiceId,
      }).unwrap()
      setPendingLcUpdatePayload(null)
      setPendingLcId(null)
      if (refetchDocuments) {
        await refetchDocuments()
      }
    } catch {
      setError('Failed to update the order. Please try again.')
    } finally {
      setLcProcessingOpen(false)
    }
  }

  const handleCancelRateChangeUpdate = async () => {
    setRatesChangedConfirmOpen(false)
    if (pendingLcId) {
      try {
        await cancelLcReprocess({ orderId, pendingId: pendingLcId }).unwrap()
      } catch {
        // Pending file may already be gone; still clear local state.
      }
    }
    setPendingLcUpdatePayload(null)
    setPendingLcId(null)
  }

  const handleFileUpload = async (event: any) => {
    const file = event.target.files[0]
    const typedDocumentType = documentType as unknown as DocumentType

    if (file) {
      try {
        if (documentType === 'LC') {
          await handleLcReprocessUpload(file)
          return
        }

        if (isPaymentDoc) {
          await addPaymentDocument({
            file,
            orderId,
            documentType: typedDocumentType,
          }).unwrap()

          setUploadOpen(false)

          if (!isPaymentError) {
            if (refetchPaymentDocuments) {
              await refetchPaymentDocuments()
            }
            if (
              documentType === 'PAYMENT_RECEIPT' &&
              invoiceType === 'NOT_PAID' &&
              invoiceId
            ) {
              setMoveToPaidConfirmOpen(true)
            }
          }
        } else {
          await addDocument({
            file,
            orderId,
            documentType: typedDocumentType,
          }).unwrap()

          setUploadOpen(false)

          if (!isAddError) {
            if (refetchDocuments) {
              await refetchDocuments()
            }
            if (
              variant === 'carrier' &&
              (documentType === 'INVOICE' || documentType === 'POD')
            ) {
              await maybeUpdateCarrierStatusAfterUpload()
            }
          }
        }
      } catch (error: any) {
        console.log(error)

        if (error?.error) {
          setError(error?.error)
        } else {
          setError('Something went wrong. Please try again later!')
        }
      }
    }
  }

  return (
    <Stack direction={'row'} alignItems={'center'} spacing={1}>
      <DocumentTypeNonDropdown
        theme={theme}
        isupload={isupload.toString()}
        condensed={condensed.toString()}
      >
        {documentFullName(documentType)}
      </DocumentTypeNonDropdown>
      {isupload && (
        <img
          src={Plus}
          alt={`add ${documentType}`}
          width={condensed ? 25 : 35}
          height={condensed ? 25 : 35}
          style={{ cursor: 'pointer' }}
          onClick={handleOpen}
        />
      )}
      <FileUpload
        open={uploadOpen}
        handleClose={() => setUploadOpen(false)}
        onFileUpload={handleFileUpload}
        title={`Upload ${documentFullName(documentType)} document`}
        isLoading={
          isAddLoading ||
          isPaymentLoading ||
          isLcReprocessLoading ||
          isLcApplyLoading
        }
        error={error}
      />
      <LoadConfirmationProcessing open={lcProcessingOpen} />
      <ConfirmationDialog
        title="Rates have changed"
        open={ratesChangedConfirmOpen}
        handleClose={handleCancelRateChangeUpdate}
        cancelBtnText="Cancel"
        successBtnText="Update order"
        successBtnTextStyle={{ color: 'primary' }}
        handleCancelBtn={handleCancelRateChangeUpdate}
        handleSuccessBtn={handleConfirmRateChangeUpdate}
      >
        The rates on this load confirmation differ from the current order. Do you
        want to update the order with the new data?
      </ConfirmationDialog>
      <ConfirmationDialog
        title="Move to Paid?"
        open={moveToPaidConfirmOpen}
        handleClose={() => setMoveToPaidConfirmOpen(false)}
        cancelBtnText="No"
        successBtnText="Yes"
        successBtnTextStyle={{ color: 'primary' }}
        handleCancelBtn={() => setMoveToPaidConfirmOpen(false)}
        handleSuccessBtn={handleConfirmMoveToPaid}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            minHeight: 180,
            '& .MuiDialogTitle-root': {
              fontSize: '1.25rem',
              fontWeight: 600,
              pb: 1,
            },
            '& .MuiDialogContentText-root': {
              fontSize: '1rem',
              lineHeight: 1.6,
              color: 'text.primary',
            },
            '& .MuiDialogActions-root': {
              px: 3,
              pb: 2.5,
              gap: 1,
            },
          },
        }}
      >
        Do you want to move this invoice to Paid?
      </ConfirmationDialog>
      {isMarkPaidLoading && <TranspotsLoading loadingText="Updating..." />}
    </Stack>
  )
}

export default InvoiceDocumentsTableDocType
